import { useMemo, useState, useCallback } from "react"
import { Button } from "~/react/ui/button"
import { Icon } from "~/react/ui/icon"
import { Tooltip, TooltipContent, TooltipTrigger } from "~/react/ui/tooltip"
import { t } from "../i18n"
import { checkSpamScore, type SpamScoreResult } from "../api"

interface EmailMetricsProps {
  body: string
  subject?: string
  campaignId: number
  step: number
}

function countWords(text: string): number {
  const cleanText = text
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim()

  if (!cleanText) return 0
  return cleanText.split(/\s+/).filter((word) => word.length > 0).length
}

function getPlainText(html: string): string {
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim()
}

function calculateReadingTime(wordCount: number): string {
  if (wordCount === 0) return "0 sec"

  // Average reading speed is 200-250 words per minute, using 225 as average
  const seconds = Math.ceil((wordCount / 225.0) * 60)

  if (seconds < 60) {
    return `${seconds} sec`
  }

  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  if (remainingSeconds === 0) {
    return `${minutes} min`
  }
  return `${minutes} min ${remainingSeconds} sec`
}

function countSyllables(word: string): number {
  word = word.toLowerCase().replace(/[^a-z]/g, "")
  if (word.length === 0) return 0

  const wordClean = word.replace(/[aeiouy]+/g, "a").replace(/[^a]/g, "")
  let syllableCount = wordClean.length

  if (word.endsWith("e") && syllableCount > 1) {
    syllableCount -= 1
  }

  if (word.length > 2 && word.endsWith("le") && !/[aeiouy]/.test(word[word.length - 3])) {
    syllableCount += 1
  }

  return Math.max(syllableCount, 1)
}

// Calculate Flesch-Kincaid Grade Level
// Formula: 0.39 * (total_words / total_sentences) + 11.8 * (total_syllables / total_words) - 15.59
// Returns U.S. school grade level (0-20), where lower grades indicate easier reading
function calculateFleschKincaidGrade(text: string): number | null {
  const sentences = text.split(/[.!?]+/).filter((s) => s.trim().length > 0)
  if (sentences.length === 0) return null

  const words = text.split(/\s+/).filter((w) => w.trim().length > 0)
  if (words.length === 0) return null

  const totalWords = words.length
  if (totalWords < 5) return null

  const totalSentences = sentences.length
  const totalSyllables = words.reduce((sum, word) => sum + countSyllables(word), 0)

  if (totalSyllables === 0) return null

  const grade = 0.39 * (totalWords / totalSentences) + 11.8 * (totalSyllables / totalWords) - 15.59

  return Math.max(0, Math.min(20, grade))
}

function getGradeExplanation(grade: number | null): string {
  if (grade === null) {
    return t("js.campaigns.content.grade_explanation.unknown")
  }

  const gradeInt = Math.floor(grade)

  if (gradeInt <= 5) {
    return t("js.campaigns.content.grade_explanation.very_easy", {
      grade: gradeInt,
    })
  } else if (gradeInt <= 8) {
    return t("js.campaigns.content.grade_explanation.easy", {
      grade: gradeInt,
    })
  } else if (gradeInt === 9) {
    return t("js.campaigns.content.grade_explanation.average", {
      grade: gradeInt,
    })
  } else if (gradeInt <= 12) {
    return t("js.campaigns.content.grade_explanation.fairly_difficult", {
      grade: gradeInt,
    })
  } else if (gradeInt <= 16) {
    return t("js.campaigns.content.grade_explanation.difficult", {
      grade: gradeInt,
    })
  } else {
    return t("js.campaigns.content.grade_explanation.very_difficult", {
      grade: gradeInt,
    })
  }
}

interface SpamScoreState {
  loading: boolean
  result: SpamScoreResult | null
}

interface SpamScoreDisplay {
  label: string
  colorClass: string
  icon: string
  tooltip: string
  isRateLimited: boolean
}

function getSpamScoreDisplay(result: SpamScoreResult): SpamScoreDisplay {
  if (result.result === "unknown") {
    if (result.reason === "rate_limited") {
      return {
        label: t("js.campaigns.content.spam_score_rate_limited"),
        colorClass: "text-muted",
        icon: "",
        tooltip: t("js.campaigns.content.spam_score_rate_limited_tooltip"),
        isRateLimited: true,
      }
    }
    if (result.reason === "email_is_too_long") {
      return {
        label: t("js.campaigns.content.spam_score_too_long"),
        colorClass: "text-muted",
        icon: "",
        tooltip: t("js.campaigns.content.spam_score_too_long_tooltip"),
        isRateLimited: false,
      }
    }
    return {
      label: t("js.campaigns.content.spam_score_unknown"),
      colorClass: "text-muted",
      icon: "",
      tooltip: t("js.campaigns.content.spam_score_unknown_tooltip"),
      isRateLimited: false,
    }
  }

  const score = result.result as number
  if (score < 30) {
    return {
      label: t("js.campaigns.content.spam_score_good"),
      colorClass: "text-success",
      icon: "face-smile-hearts",
      tooltip: result.reason as string,
      isRateLimited: false,
    }
  }
  if (score < 70) {
    return {
      label: t("js.campaigns.content.spam_score_okay"),
      colorClass: "text-warning",
      icon: "face-rolling-eyes",
      tooltip: result.reason as string,
      isRateLimited: false,
    }
  }
  return {
    label: t("js.campaigns.content.spam_score_spammy"),
    colorClass: "text-danger",
    icon: "face-pensive",
    tooltip: result.reason as string,
    isRateLimited: false,
  }
}

export function EmailMetrics({ body, subject = "", campaignId, step }: EmailMetricsProps) {
  const [spamScore, setSpamScore] = useState<SpamScoreState>({
    loading: false,
    result: null,
  })

  const metrics = useMemo(() => {
    const bodyText = getPlainText(body)
    const subjectText = subject.trim()
    const fullText = [subjectText, bodyText].filter((t) => t.length > 0).join(" ")

    const wordCount = countWords(body)
    const readingTime = calculateReadingTime(fullText.split(/\s+/).filter((w) => w.length > 0).length)
    const grade = calculateFleschKincaidGrade(fullText)
    const gradeDisplay = grade === null ? "-" : Math.floor(grade).toString()
    const gradeExplanation = getGradeExplanation(grade)

    return { wordCount, readingTime, gradeDisplay, gradeExplanation }
  }, [body, subject])

  const handleCheckSpamScore = useCallback(async () => {
    setSpamScore({ loading: true, result: null })
    const result = await checkSpamScore({ campaignId, step })
    setSpamScore({ loading: false, result })
  }, [campaignId, step])

  const renderSpamScoreContent = () => {
    // Initial state - just show "Check" button
    if (!spamScore.loading && !spamScore.result) {
      return (
        <Button variant="linkGrey" size="sm" onClick={handleCheckSpamScore}>
          {t("js.campaigns.content.check_spam")}
        </Button>
      )
    }

    // Loading state - show spinner with "Checking..." text
    if (spamScore.loading) {
      return (
        <span className="text-muted">
          <Icon icon="spinner-third" variant="regular" className="fa-spin" aria-hidden="true" />{" "}
          {t("js.campaigns.content.spam_score_checking")}
        </span>
      )
    }

    // Result state - show result with tooltip and "Check again" button
    const display = getSpamScoreDisplay(spamScore.result!)

    return (
      <>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={display.colorClass}>
              {display.label}
              {display.icon && (
                <>
                  {" "}
                  <Icon icon={display.icon} variant="regular" aria-hidden="true" />
                </>
              )}
            </span>
          </TooltipTrigger>
          <TooltipContent>{display.tooltip}</TooltipContent>
        </Tooltip>
        {" - "}
        {display.isRateLimited ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button variant="linkGrey" size="sm" disabled>
                  {t("js.campaigns.content.spam_score_check_again")}
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>{t("js.campaigns.content.spam_score_rate_limited_tooltip")}</TooltipContent>
          </Tooltip>
        ) : (
          <Button variant="linkGrey" size="sm" onClick={handleCheckSpamScore}>
            {t("js.campaigns.content.spam_score_check_again")}
          </Button>
        )}
      </>
    )
  }

  return (
    <footer className="compose-message-footer">
      <dl className="compose-message-metas">
        <div className="compose-message-meta">
          <dt className="compose-message-meta__label">{t("js.campaigns.content.words")}</dt>
          <dd className="compose-message-meta__value">{metrics.wordCount}</dd>
        </div>

        <div className="compose-message-meta">
          <dt className="compose-message-meta__label">{t("js.campaigns.content.reading_time")}</dt>
          <dd className="compose-message-meta__value">{metrics.readingTime}</dd>
        </div>

        <div className="compose-message-meta">
          <dt className="compose-message-meta__label">{t("js.campaigns.content.grade")}</dt>
          <dd className="compose-message-meta__value">
            <Tooltip>
              <TooltipTrigger asChild>
                <span>{metrics.gradeDisplay}</span>
              </TooltipTrigger>
              <TooltipContent>{metrics.gradeExplanation}</TooltipContent>
            </Tooltip>
          </dd>
        </div>

        <div className="compose-message-meta">
          <dt className="compose-message-meta__label">{t("js.campaigns.content.spam_score")}</dt>
          <dd className="compose-message-meta__value">{renderSpamScoreContent()}</dd>
        </div>
      </dl>
    </footer>
  )
}
